import React, { useState, Suspense, useContext, useEffect } from "react";
import Modal from "../common/Modal";
import GameTypePickerModal from "../modals/GameTypePickerModal";
import RemovePlayersModal from "../modals/RemovePlayersModal";
import GameRoomContext from "../../context/GameRoomContext";
import SocketContext from "../../context/SocketContext";
import UIContext from "../../context/UIContext";
import UserContext from "../../context/UserContext";
import { isLocalPlayerId } from "../../utils/normalizePlayerId";

import GAME_COMPONENTS from "../games";

function GameRoomHeading(props) {
  const [showInfo, setShowInfo] = useState(false);
  const [showGameTypeModal, setShowGameTypeModal] = useState(false);
  const [showRemovePlayersModal, setShowRemovePlayersModal] = useState(false);
  const [pendingGameTypeSelection, setPendingGameTypeSelection] = useState(null);
  const [showGotoConfigConfirm, setShowGotoConfigConfirm] = useState(false);

  // Contexts
  const contextSocket = useContext(SocketContext);
  const { user } = useContext(UserContext);
  // user is available from context if needed via GameRoomContext or Socket; avoid unused variable here
  const { gameTypes: uiGameTypes } = useContext(UIContext);
  const { gameRoomData, onChangeGameType } = useContext(GameRoomContext);

  // Prefer props when provided by caller (caller may already have these values),
  // otherwise fall back to values from context to avoid duplicate declarations.
  const configSchema =
    props.configSchema || gameRoomData?.gameRoomConfigSchema || {};
  const contextGameState = gameRoomData?.gameRoomGame?.gameState || {};
  const gameState = props.gameState || contextGameState;
  const socket = props.socket || contextSocket;
  const roomId = gameRoomData?.gameRoomDetails?.roomId || "";
  const gameRoomHostUserId = gameRoomData?.gameRoomDetails?.hostUserId || "";
  const gameRoomHostSocketId = gameRoomData?.gameRoomDetails?.hostSocketId || "";
  const currentUserId = user?.userId || socket?.auth?.userId || "";
  const isHost =
    typeof props.isHost !== "undefined"
      ? props.isHost
      : !!(
          (currentUserId && currentUserId === gameRoomHostUserId) ||
          (socket?.id && socket.id === gameRoomHostSocketId)
        );
  const gameTypes = props.gameTypes || uiGameTypes;
  const gameInStartedPhase = props.gameInStartedPhase;
  const serverFinished = props.serverFinished;
  const [showTurnText, setShowTurnText] = useState(false);

  // Determine if betting is closed: finished, showdown stage, or all remaining players are all-in
  const seatsForBet = gameState?.seats || [];
  const activeSeats = seatsForBet.filter((s) => !s?.folded);
  const activeNotAllIn = activeSeats.filter((s) => !s?.allIn);
  const bettingClosed = !!(
    gameState?.finished ||
    gameState?.stage === "showdown" ||
    (activeNotAllIn.length === 0 && activeSeats.length > 0)
  );

  const isMyTurn = !!(
    gameState?.isTurnBased &&
    gameInStartedPhase &&
    !serverFinished &&
    !bettingClosed &&
    isLocalPlayerId(
      gameState?.currentPlayerSocketId || gameState?.currentPlayer,
      gameRoomData?.gameRoomPlayers,
      socket,
      user || null
    )
  );

  useEffect(() => {
    let t;
    if (gameState?.isTurnBased && !gameState.finished && !gameState.showWinningState) {
      // start immediately showing the turn text, then toggle each second
      setShowTurnText(true);
      t = setInterval(() => setShowTurnText((v) => !v), 1000);
    } else {
      setShowTurnText(false);
    }

    return () => clearInterval(t);
  }, [gameState?.currentPlayer, gameState?.isTurnBased, gameState?.finished, gameState?.showWinningState]);

  const name = configSchema?.displayName;
  const gameType = configSchema?.gameClass || configSchema?.displayName || "";
  // Get GuideComponent from GAME_COMPONENTS static property
  const GameComponent = GAME_COMPONENTS[gameType];
  const GuideComponent = GameComponent?.Guide;

  return (
    <div className="flex items-center mb-4 justify-between">
      <div className="flex items-center">
        {/* Info button left of the title */}
        <button
          type="button"
          aria-label="Game info"
          className="mr-2 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none"
          onClick={() => setShowInfo(true)}
          style={{ fontSize: "1.1em", lineHeight: 1 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <g
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            >
              <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0-18 0m9-3h.01" />
              <path d="M11 12h1v4h1" />
            </g>
          </svg>
        </button>

        {/* Alternate the heading text each second between the game name and "It's your turn" */}
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mr-2">
          {gameState?.isTurnBased && showTurnText ? (
            isMyTurn ? (
              <span
                className="bg-yellow-400 dark:bg-yellow-500 text-black dark:text-black px-3 py-1 rounded shadow-lg "
                style={{ pointerEvents: "none" }}
              >
                Your go!
              </span>
            ) : (
              (() => {
                const player = (gameRoomData.gameRoomPlayers || []).find(
                  (p) => (p.userid || p.socketId) === gameState?.currentPlayer
                );
                if (!player) return name;
                return (
                  <span
                    className="bg-blue-400 dark:bg-blue-500 text-black dark:text-black px-3 py-1 rounded shadow-lg"
                    style={{ pointerEvents: "none" }}
                  >
                    <span style={{ maxWidth: '12ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {player.username}
                    </span>
                    <span style={{ marginLeft: 0, whiteSpace: 'nowrap' }}>'s Turn</span>
                  </span>
                );
              })()
            )
          ) : (
            name
          )}
        </h2>
      </div>

      {/* Right-aligned controls (host-only Change Game button) */}
      <div className="flex items-center gap-2">
        {gameState && isHost && (
          <>
            <button
              className="ml-1 px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm border dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-blue-100 flex items-center"
              title="Change Game Type"
              onClick={() => setShowGameTypeModal(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <g
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                >
                  <path d="M3 18a2 2 0 1 0 4 0a2 2 0 1 0-4 0M17 6a2 2 0 1 0 4 0a2 2 0 1 0-4 0" />
                  <path d="M19 8v5a5 5 0 0 1-5 5h-3l3-3m0 6l-3-3m-6-2v-5a5 5 0 0 1 5-5h3l-3-3m0 6l3-3" />
                </g>
              </svg>
              <span className="hidden sm:inline">&nbsp;Change Game</span>
            </button>
            {showGameTypeModal && (
              <GameTypePickerModal
                showGameTypePicker={showGameTypeModal}
                gameTypes={gameTypes}
                closeGameTypePicker={() => setShowGameTypeModal(false)}
                onSelect={(key) => {
                  // Intercept selection: if the selected game's maxPlayers is less than current players,
                  // prompt host to choose players to remove first.
                  setShowGameTypeModal(false);
                  const selected = Array.isArray(gameTypes)
                    ? gameTypes.find((gt) => gt.gameClass === key || gt.key === key)
                    : Object.values(gameTypes || {}).find((gt) => gt.gameClass === key || gt.key === key);
                  const maxPlayers = selected && typeof selected.maxPlayers === 'number' ? selected.maxPlayers : Infinity;
                  const playersArr = Array.isArray(gameRoomData?.gameRoomPlayers) ? gameRoomData.gameRoomPlayers : [];
                  if (playersArr.length > maxPlayers) {
                    // need to remove players
                    setPendingGameTypeSelection(key);
                    setShowRemovePlayersModal(true);
                  } else {
                    if (onChangeGameType) onChangeGameType(roomId, key);
                  }
                }}
                // Prefer the configured gameClass, fall back to runtime gameState.gameType
                selectedKey={configSchema?.gameClass || gameState?.gameType}
                mode="grid"
                title="Change Game Type"
              />
            )}

              {/* Gear: go back to current game's settings (host-only) */}
              <>
                <button
                  className="ml-1 px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm border dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-blue-100 flex items-center"
                  title="Edit Current Game Settings"
                  onClick={() => setShowGotoConfigConfirm(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.4 13.6a8 8 0 0 0 0-3.2l2-1.6-2-3.464-2.4.8a8.1 8.1 0 0 0-2.6-1.5L14 2h-4l-.8 3.336a8.1 8.1 0 0 0-2.6 1.5l-2.4-.8L2 9.8l2 1.6a8 8 0 0 0 0 3.2l-2 1.6 2 3.464 2.4-.8a8.1 8.1 0 0 0 2.6 1.5L10 22h4l.8-3.336a8.1 8.1 0 0 0 2.6-1.5l2.4.8 2-3.464-2-1.6z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  
                </button>
                {showGotoConfigConfirm && (
                  <Modal show={showGotoConfigConfirm} title={`Edit ${configSchema?.displayName || 'Game'} Settings`} onClose={() => setShowGotoConfigConfirm(false)}>
                    <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800 text-center">
                      <p className="mb-4 text-lg dark:text-gray-100">End the current game and return to the configuration page?</p>
                      <div className="flex justify-center mt-4">
                        <button
                          className="bg-blue-500 text-white p-2 rounded mr-2"
                          onClick={() => {
                            // Request server to revert the game to config phase
                            setShowGotoConfigConfirm(false);
                            try {
                              const sock = props.socket || contextSocket;
                              if (sock && roomId) {
                                try {
                                  sock.emit('revertToConfig', { roomId }, (resp) => {
                                    // Ignore detailed response; server will emit new room state
                                  });
                                } catch (e) {}
                              }
                            } catch (e) {}
                          }}
                        >
                          Yes, Edit Settings
                        </button>
                        <button
                          className="bg-gray-300 p-2 rounded dark:bg-gray-600 dark:text-gray-100"
                          onClick={() => setShowGotoConfigConfirm(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </Modal>
                )}
              </>

            {showRemovePlayersModal && (
              <RemovePlayersModal
                show={showRemovePlayersModal}
                players={gameRoomData?.gameRoomPlayers || []}
                maxKeep={(() => {
                  const sel = Array.isArray(gameTypes)
                    ? gameTypes.find((gt) => gt.gameClass === pendingGameTypeSelection || gt.key === pendingGameTypeSelection)
                    : Object.values(gameTypes || {}).find((gt) => gt.gameClass === pendingGameTypeSelection || gt.key === pendingGameTypeSelection);
                  return sel && typeof sel.maxPlayers === 'number' ? sel.maxPlayers : Infinity;
                })()}
                onClose={() => { setShowRemovePlayersModal(false); setPendingGameTypeSelection(null); }}
                hostUserId={gameRoomData?.gameRoomDetails?.hostUserId}
                hostSocketId={gameRoomData?.gameRoomDetails?.hostSocketId}
                allowSpectators={gameRoomData?.gameRoomDetails?.allowSpectators !== false}
                onConfirm={(selectedIds) => {
                  setShowRemovePlayersModal(false);
                  // Call onChangeGameType with selectedPlayerIds so server can remove them before changing game
                  if (onChangeGameType) onChangeGameType(roomId, pendingGameTypeSelection, selectedIds);
                  setPendingGameTypeSelection(null);
                }}
              />
            )}
          </>
        )}
      </div>

      {showInfo && (
        <Modal show={showInfo} title={name} onClose={() => setShowInfo(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl p-4">
            {GuideComponent ? (
              // If GuideComponent is a lazy component, wrap in Suspense
              typeof GuideComponent === "object" &&
              GuideComponent !== null &&
              GuideComponent.$$typeof === Symbol.for("react.lazy") ? (
                <Suspense fallback={<div>Loading guide...</div>}>
                  <GuideComponent />
                </Suspense>
              ) : (
                <GuideComponent />
              )
            ) : (
              <div className="text-gray-700 dark:text-gray-200">
                No guide available.
              </div>
            )}
            <button
              className="mt-4 bg-blue-500 dark:bg-blue-700 text-white px-4 py-2 rounded hover:bg-blue-600 dark:hover:bg-blue-600"
              onClick={() => setShowInfo(false)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default GameRoomHeading;
